import { AutoCustomCompleteField, CustomDateControl, CustomSelect, CustomTimefield, FormField, FormGrid, FormShell, Input, MessageAlert, Radio, RBButton } from '@scmlatam/web-ui';
import type { AddPaycodeDialogProps, AlertProps, FooterProps } from '../types/component';

const Alert = ({ t, validationMessage, setValidationMessage, showMessage, setShowMessage, onClickCancel }: AlertProps) => {
    if (validationMessage) {
        return (
            <div id="schedule-add-paycode-alert-validation">
                <MessageAlert
                    type="warning"
                    title={validationMessage}
                    message=""
                    className="rb-message-alert--form"
                    actions={[
                        {
                            label: 'OK',
                            onClick: () => setValidationMessage(''),
                        },
                    ]}
                />
            </div>
        );
    }

    if (showMessage) {
        return (
            <div id="schedule-add-paycode-alert-unsaved">
                <MessageAlert
                    type="warning"
                    title={t('add_paycode.alerts.unsaved_changes.title')}
                    message={t('add_paycode.alerts.unsaved_changes.message')}
                    className="rb-message-alert--form"
                    actions={[
                        {
                            label: t('add_paycode.alerts.unsaved_changes.action_button'),
                            onClick: () => setShowMessage(false),
                        },
                        {
                            label: t('add_paycode.alerts.unsaved_changes.cancel_button'),
                            onClick: () => {
                                setShowMessage(false);
                                onClickCancel();
                            },
                        },
                    ]}
                />
            </div>
        );
    }

    return undefined;
};

const Footer = ({ t, loading, onClickCancel, onClickSave }: FooterProps) => (
    <div id="schedule-add-paycode-footer" className="flex flex-wrap items-center justify-end gap-3 p-3">
        <RBButton id="schedule-add-paycode-cancel" variant="secondary" size="m" disabled={loading} onClick={onClickCancel}>
            {t('add_paycode.buttons.cancel')}
        </RBButton>
        <RBButton id="schedule-add-paycode-save" size="m" disabled={loading} onClick={onClickSave}>
            {t('add_paycode.buttons.add')}
        </RBButton>
    </div>
);

const AddPaycodeDialog = ({ formProps }: AddPaycodeDialogProps) => {
    const { formData, setFormData, t, handleCancel } = formProps;

    return (
        <FormShell
            open={formProps.openForm}
            title={formProps.title}
            size="m"
            onClose={handleCancel}
            loading={formProps.loading}
            alert={
                <Alert
                    t={t}
                    validationMessage={formProps.validationMessage}
                    setValidationMessage={formProps.setValidationMessage}
                    showMessage={formProps.showMessage}
                    setShowMessage={formProps.setShowMessage}
                    onClickCancel={formProps.closeForm}
                />
            }
            footer={<Footer t={t} loading={formProps.loading} onClickCancel={handleCancel} onClickSave={formProps.submit} />}
        >
            <FormGrid id="schedule-add-paycode-form">
                <FormField span={12}>
                    <AutoCustomCompleteField
                        id="schedule-add-paycode-workers"
                        label={t('add_paycode.fields.worker')}
                        placeholder={t('add_paycode.placeholder.select')}
                        values={formProps.employees}
                        value={formProps.employeeIds}
                        setValue={formProps.setEmployeeIds}
                        selectionMode="multiple"
                        white
                        error={formProps.validationErrors.employeeIds.error}
                        helperText={formProps.validationErrors.employeeIds.error ? formProps.validationErrors.employeeIds.message : ''}
                        search={{
                            value: formProps.searchEmployee,
                            onChange: formProps.setSearchEmployee,
                            placeholder: t('add_paycode.placeholder.select'),
                            mode: 'local',
                            emptyText: t('add_paycode.placeholder.select'),
                        }}
                    />
                </FormField>

                <FormField span={12}>
                    <AutoCustomCompleteField
                        id="schedule-add-paycode-paycode"
                        label={t('add_paycode.fields.paycode')}
                        placeholder={t('add_paycode.placeholder.select')}
                        values={formProps.paycodes}
                        value={formData.paycode}
                        setValue={(paycode: any) => setFormData({ ...formData, paycode })}
                        disabled={formProps.loadingPaycodes}
                        selectionMode="single"
                        white
                        error={formProps.validationErrors.paycode.error}
                        helperText={formProps.validationErrors.paycode.error ? formProps.validationErrors.paycode.message : ''}
                        search={{
                            value: formProps.searchPaycode,
                            onChange: formProps.setSearchPaycode,
                            placeholder: t('add_paycode.placeholder.select'),
                            mode: 'local',
                            emptyText: t('add_paycode.placeholder.select'),
                        }}
                    />
                </FormField>

                <FormField span={12}>
                    <div id="schedule-add-paycode-mode" className="flex h-full items-center gap-3">
                        <span className="text-md font-medium">{t('add_paycode.fields.type_amount')}</span>
                        <Radio
                            id="schedule-add-paycode-mode-relative"
                            name="paycode-mode"
                            value="relative_amount"
                            checked={formData.mode === 'relative_amount'}
                            onChange={() => setFormData({ ...formData, mode: 'relative_amount' })}
                        >
                            {t('add_paycode.fields.relative_amount')}
                        </Radio>
                        <Radio
                            id="schedule-add-paycode-mode-numerical"
                            name="paycode-mode"
                            value="numerical_amount"
                            checked={formData.mode === 'numerical_amount'}
                            onChange={() => setFormData({ ...formData, mode: 'numerical_amount' })}
                        >
                            {t('add_paycode.fields.numerical_amount')}
                        </Radio>
                    </div>
                </FormField>

                <FormField id="schedule-add-paycode-date" span={6}>
                    <CustomDateControl
                        label={t('add_paycode.fields.date')}
                        initialValue={null}
                        value={formData.date}
                        setValue={(date) => setFormData({ ...formData, date })}
                        disabled={false}
                        white
                        fullWidth
                        error={formProps.validationErrors.date.error}
                        helperText={formProps.validationErrors.date.error ? formProps.validationErrors.date.message : ''}
                    />
                </FormField>

                <FormField id="schedule-add-paycode-start" span={6}>
                    <CustomTimefield
                        label={t('add_paycode.fields.start_at')}
                        initialValue={null}
                        value={formData.start}
                        setValue={(start) => setFormData({ ...formData, start })}
                        disabled={false}
                        white
                        fullWidth
                        error={formProps.validationErrors.start.error}
                        helperText={formProps.validationErrors.start.error ? formProps.validationErrors.start.message : ''}
                    />
                </FormField>

                <FormField span={4}>
                    <Input
                        id="schedule-add-paycode-repeat"
                        type="number"
                        label={t('add_paycode.fields.repeat')}
                        labelVariant="notched"
                        value={formData.repeat}
                        onChange={(event) => setFormData({ ...formData, repeat: event.target.value })}
                        min={1}
                        error={formProps.validationErrors.repeat.error}
                        helperText={formProps.validationErrors.repeat.error ? formProps.validationErrors.repeat.message : ''}
                    />
                </FormField>

                <FormField span={4}>
                    <Input
                        id="schedule-add-paycode-amount-days"
                        type="number"
                        label={t('add_paycode.fields.amount_of_days')}
                        labelVariant="notched"
                        value={formData.amountDays}
                        onChange={(event) => setFormData({ ...formData, amountDays: event.target.value })}
                        min={0.5}
                        max={1}
                        step={0.5}
                        error={formProps.validationErrors.amountDays.error}
                        helperText={formProps.validationErrors.amountDays.error ? formProps.validationErrors.amountDays.message : ''}
                    />
                </FormField>

                <FormField id={formData.mode === 'numerical_amount' ? 'schedule-add-paycode-amount-hours-numerical' : 'schedule-add-paycode-amount-hours-relative'} span={4}>
                    {formData.mode === 'numerical_amount' ? (
                        <CustomTimefield
                            label={t('add_paycode.fields.amount_of_hours')}
                            initialValue={null}
                            value={formData.amountHours}
                            setValue={(amountHours) => setFormData({ ...formData, amountHours })}
                            disabled={false}
                            white
                            fullWidth
                            error={formProps.validationErrors.amountHours.error}
                            helperText={formProps.validationErrors.amountHours.error ? formProps.validationErrors.amountHours.message : ''}
                        />
                    ) : (
                        <CustomSelect
                            label={t('add_paycode.fields.amount_of_hours')}
                            value={String(formData.amountRelativeHours)}
                            setValue={(value: any) =>
                                setFormData({
                                    ...formData,
                                    amountRelativeHours: (Number(value) === 2 ? 2 : 1) as 1 | 2,
                                })
                            }
                            values={[
                                {
                                    label: t('add_paycode.fields.planned_full_day'),
                                    value: '1',
                                },
                                {
                                    label: t('add_paycode.fields.planned_half_day'),
                                    value: '2',
                                },
                            ]}
                            placeholder={t('add_paycode.placeholder.select')}
                            disabled={false}
                            white
                            fullWidth
                        />
                    )}
                </FormField>

                <FormField span={12}>
                    <Input
                        id="schedule-add-paycode-note"
                        as="textarea"
                        label={t('add_paycode.fields.note')}
                        labelVariant="notched"
                        placeholder={t('add_paycode.placeholder.note')}
                        value={formData.note}
                        onChange={(event) => setFormData({ ...formData, note: event.target.value })}
                    />
                </FormField>

                <FormField span={12}>
                    <Input
                        id="schedule-add-paycode-comment"
                        as="textarea"
                        label={t('add_paycode.fields.comment')}
                        labelVariant="notched"
                        placeholder={t('add_paycode.placeholder.comment')}
                        value={formData.comment}
                        onChange={(event) => setFormData({ ...formData, comment: event.target.value })}
                    />
                </FormField>
            </FormGrid>
        </FormShell>
    );
};

export { AddPaycodeDialog };
